import {createStore} from 'redux';
import {GeoJsonCore, GeoJsonFeature, RectAngle, SearchResult, SearchType} from '../schema';
import {
  setText, SetTextAction,
  setGJFs, SetGJFsAction,
  setSearchResults, SetSearchResultsAction,
  pointOnMap, PointOnMapAction,
  MoveMapCenterAction, moveMapCenter,
  setRectAngle, SetRectAngleAction,
  setSearchType, SetSearchTypeAction,
  SetActiveArticleAction, setActiveArticle,
  setActiveLocation, SetActiveLocationAction,
  setMapBounds, SetMapBoundsAction,
} from './types';

export type Actions = SetTextAction | SetGJFsAction | SetRectAngleAction | SetSearchTypeAction
  | SetSearchResultsAction | PointOnMapAction | MoveMapCenterAction | SetActiveArticleAction
  | SetActiveLocationAction | SetMapBoundsAction;

export type RootState = {
  text: string;
  pointerFeature?: GeoJsonCore | undefined;
  gjfs: {
    all: GeoJsonFeature[];
    article: GeoJsonFeature[];
    focus: GeoJsonFeature[];
  };
  searchResults: SearchResult[];
  mapCenter: [number, number];
  rectAngle: RectAngle,
  searchType: SearchType;
  activeArticleId: number | undefined;
  activeLocation: string | undefined;
  mapBounds: {minLngLat: [number, number], maxLngLat: [number, number]} | undefined;
};

const initializeState = (): RootState => ({
  text: '',
  pointerFeature: undefined,
  gjfs: {all: [], article: [], focus: []},
  searchResults: [],
  mapCenter: [0, 0],
  rectAngle: {minLngLat: [NaN, NaN], maxLngLat: [NaN, NaN]},
  searchType: 'distance',
  activeArticleId: undefined,
  activeLocation: undefined,
  mapBounds: undefined,
});

export const reducer = (state: RootState = initializeState(), action: Actions): RootState => {
  switch (action.type) {
    case 'SET_TEXT':
      return {...state, text: action.payload.text};
    case 'SET_GEOJSON_FEATURES':
      return {
        ...state,
        gjfs: {
          ...state.gjfs,
          [action.payload.target]: action.payload.features,
        },
      };
    case 'POINT_ON_MAP':
      if (action.payload.lnglat === undefined) {
        return {...state, pointerFeature: undefined};
      } else {
        return {...state, pointerFeature: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: action.payload.lnglat,
          },
        }};
      }
    case 'MOVE_MAP_CENTER':
      return {...state, mapCenter: action.payload.lnglat};
    case 'SET_SEARCH_TYPE':
      return {
        ...state,
        searchType: action.payload.searchType,
        pointerFeature: action.payload.searchType === 'distance' ?
          state.pointerFeature :
          undefined,
      };
    case 'SET_SEARCH_RESULTS':
      return {
        ...state,
        searchResults: action.payload.results,
        activeArticleId: undefined,
      };
    case 'SET_RECT_ANGLE':
      return {...state, rectAngle: {...action.payload}};
    case 'SET_ACTIVE_ARTICLE':
      return {
        ...state,
        activeArticleId: action.payload.key,
        activeLocation: undefined,
      };
    case 'SET_ACTIVE_LOCATION':
      return {...state, activeLocation: action.payload.key};
    case 'SET_MAP_BOUNDS':
      return {...state, mapBounds: {...action.payload}};
    default:
      return state;
  }
};

export const actionCreator = {
  setText,
  setGJFs,
  setSearchResults,
  pointOnMap,
  moveMapCenter,
  setRectAngle,
  setSearchType,
  setActiveArticle,
  setActiveLocation,
  setMapBounds,
};

export const store = createStore(reducer);
